import { createContext, useMemo, useContext, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';
import { useExtensionsDevtools } from '@interhyp/platform-devtools';

const extensionContext = /*#__PURE__*/createContext({
  extensions: [],
  toggles: []
});

function ExtensionProvider({
  children,
  extensions,
  toggles
}) {
  const value = useMemo(() => {
    return {
      extensions,
      toggles: toggles ?? []
    };
  }, [extensions, toggles]);
  return /*#__PURE__*/jsx(extensionContext.Provider, {
    value: value,
    children: children
  });
}

const getCaller = () => {
  try {
    const {
      stack
    } = new Error('error');
    const callerLine = stack?.split('\n')[8].trim().split(' ');
    return {
      name: callerLine?.[1] ?? 'anonymous',
      url: callerLine?.[2]?.replace('(', '').replace(')', '')
    };
  } catch {
    return {
      name: 'anonymous'
    };
  }
};

function useExtension(token, allowMultiple) {
  const {
    extensions,
    toggles
  } = useContext(extensionContext);
  const {
    addExtensionPoint,
    removeExtensionPoint
  } = useExtensionsDevtools();
  const caller = useMemo(() => {
    return getCaller();
  }, []);
  useEffect(() => {
    const extensionPoint = {
      caller,
      token
    };
    addExtensionPoint(extensionPoint);
    return () => {
      removeExtensionPoint(extensionPoint);
    };
  }, [caller, token, addExtensionPoint, removeExtensionPoint]);
  return useMemo(() => {
    const matchingPlugins = extensions.filter(p => p.token === token).filter(ext => {
      if (!ext.toggles) {
        return true;
      }
      return ext.toggles.every(toggle => toggles.includes(toggle));
    }).map(p => p.value);
    const areMultipleDefined = matchingPlugins.length > 1;
    if (allowMultiple) {
      return matchingPlugins;
    }
    if (areMultipleDefined) {
      throw new Error(`Multiple extensions for ${token.description} are defined.`);
    }
    return matchingPlugins[0];
  }, [allowMultiple, extensions, toggles, token]);
}

function createExtensionPoint(token, allowMultiple) {
  const extensionPoint = {
    createExtension: (value, toggles) => {
      return {
        token,
        value,
        toggles
      };
    },
    register: value => {
      return {
        token,
        value
      };
    }
  };
  if (allowMultiple) {
    return {
      ...extensionPoint,
      useExtensions: () => {
        return useExtension(token, true);
      },
      usePlugin: () => {
        return useExtension(token, true);
      }
    };
  }
  return {
    ...extensionPoint,
    useExtension: () => {
      return useExtension(token);
    },
    usePlugin: () => {
      return useExtension(token);
    }
  };
}

export { ExtensionProvider, createExtensionPoint };
//# sourceMappingURL=index.js.map
